import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CameraPlaybackPanel } from "../../components/flashbackSections/CameraPlaybackPanel.tsx";
import { StatePanel } from "../../components/flashbackSections/StatePanel.tsx";
import { StatusPill } from "../../components/flashbackSections/StatusPill.tsx";
import { camerasApi } from "../../api/flashbackApi/cameras.ts";
import { HttpError } from "../../api/flashbackApi/http.ts";
import type { FlashbackPlaybackLocationState } from "../../components/flashbackSections/FlashbackSectionTabs.tsx";
import type { Camera } from "../../api/flashbackApi/types.ts";
import "../../components/flashbackSections/flashbackPlayback.css";
import "../../styles/omniWatchPlace360.css";

export default function CameraPlaybackPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const playbackState =
    (location.state as FlashbackPlaybackLocationState | null) ?? null;
  const fromTab = playbackState?.fromTab ?? "live";

  const handleBack = useCallback(() => {
    navigate("/flashback", { state: { tab: fromTab } });
  }, [navigate, fromTab]);
  const { id } = useParams<{ id: string }>();
  const cameraId = id?.trim() ?? "";
  const [camera, setCamera] = useState<Camera | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCamera = useCallback(
    async (signal?: AbortSignal) => {
      if (!cameraId) {
        setCamera(null);
        setError("Invalid camera id");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const nextCamera = await camerasApi.get(cameraId, signal);
        setCamera(nextCamera);
      } catch (err) {
        if (signal?.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to load camera";
        setError(message);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [cameraId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadCamera(controller.signal);
    return () => controller.abort();
  }, [loadCamera]);

  return (
    <div className="fb-playback fb-playback--page">
      <header className="fb-playback__header">
        <div className="fb-playback__header-inner">
          <button
            type="button"
            onClick={handleBack}
            className="fb-playback__back-btn"
          >
            Back
          </button>

          <div>
            <h1 className="fb-playback__title">
              {camera ? camera.name : "Camera playback"}
            </h1>
            <p className="fb-playback__subtitle">
              Live view, recording history, and timeline
            </p>
          </div>

          {camera ? (
            <div className="fb-playback__header-meta">
              <StatusPill status={camera.status} />
              <span>{camera.recordingMode}</span>
            </div>
          ) : null}
        </div>
      </header>

      {error ? (
        <main className="fb-playback__main fb-playback__main--centered">
          <StatePanel
            variant="error"
            title="Could not load playback"
            description={error}
            action={
              <button
                type="button"
                onClick={() => void loadCamera()}
                className="fb-playback__retry-btn"
              >
                Retry
              </button>
            }
          />
        </main>
      ) : isLoading ? (
        <main className="fb-playback__main fb-playback__main--centered">
          <StatePanel
            title="Loading playback..."
            description="Fetching camera details and timeline."
          />
        </main>
      ) : camera ? (
        <main className="fb-playback__main fb-playback__main--panel">
          <CameraPlaybackPanel
            camera={camera}
            initialDate={playbackState?.date}
          />
        </main>
      ) : null}
    </div>
  );
}
